const DEFAULT_MAX_LENGTH = 80;
const DEFAULT_FALLBACK = 'Attached document';
// Strip ASCII control characters (incl. newlines) from server- and OS-supplied
// filenames before rendering. `no-control-regex` is exactly the wrong rule
// here — the point IS to match `\x00-\x1F\x7F`.
// eslint-disable-next-line no-control-regex
const CONTROL_CHAR_RE = /[\x00-\x1F\x7F]/g;

interface ISanitizeFilenameOptions {
  /** Max rendered length; longer names truncate with a trailing `…`. Defaults to 80. */
  maxLength?: number;
  /** String returned when the input is `null` / empty / control-chars-only. Defaults to `"Attached document"`. */
  fallback?: string;
}

/**
 * Render a server- or OS-supplied filename safely.
 *
 *  - Strips ASCII control chars (`\x00–\x1F`, `\x7F`) — defends against
 *    layout-breaking newlines and copy-paste smuggled escape sequences.
 *  - Truncates to `maxLength` chars (default 80) with a trailing `…`.
 *  - Returns `fallback` (default `"Attached document"`) when the input is
 *    `null`, empty, or reduces to nothing after stripping.
 *
 * Used wherever the assistant attachment surface renders a filename — the
 * composer's per-file chip, the user-turn attachment chip in the message
 * bubble, and any future preview / list view. Keep new attachment UIs
 * routed through this helper rather than redoing the truncate / strip
 * dance per call site.
 */
const sanitizeFilename = (raw: string | null, options: ISanitizeFilenameOptions = {}): string => {
  const maxLength = options.maxLength ?? DEFAULT_MAX_LENGTH;
  const fallback = options.fallback ?? DEFAULT_FALLBACK;
  if (raw === null || raw.length === 0) {
    return fallback;
  }
  const cleaned = raw.replace(CONTROL_CHAR_RE, '');
  if (cleaned.length === 0) {
    return fallback;
  }
  if (cleaned.length <= maxLength) {
    return cleaned;
  }
  return `${cleaned.slice(0, maxLength - 1)}…`;
};

export default sanitizeFilename;
